import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { produce } from 'immer';
import {
  ProjectHistory,
  touchProject,
  createEmptyPage,
  type PhotobookProject,
  type PageDocument,
  type PageElement,
  type AssetReference,
} from '@fotoboek/domain';
import { applyTemplateToPage as applyTemplateToPageDoc } from '@fotoboek/templates';
import { debounce } from '../lib/debounce';
import {
  saveProject,
  loadProject as loadProjectFromRepo,
  saveThumbnail,
} from '../storage/projectRepository';
import { loadAssetObjectUrl, removePhotoAsset } from '../photos/importPhotos';
import { pushProjectSync } from '../cloud/syncClient';

export type CoverPanelKey = 'front' | 'back' | 'spine';

export type SelectionTarget =
  { scope: 'page'; pageId: string } | { scope: 'cover'; panel: CoverPanelKey };

export type Selection = {
  target: SelectionTarget | null;
  elementIds: string[];
};

export type ViewState = { mode: 'cover' } | { mode: 'spread'; spreadIndex: number };

export type AssetRuntime = {
  objectUrl: string;
  thumbnailUrl: string;
  widthPx: number;
  heightPx: number;
};

export type ProjectStatus = 'idle' | 'loading' | 'ready' | 'error';
export type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error';
export type CloudSyncStatus = 'idle' | 'syncing' | 'synced' | 'error' | 'offline';

function reindexPages(pages: PageDocument[]): PageDocument[] {
  return pages.map((p, i) => ({ ...p, order: i }));
}

function cloneElementWithNewId(el: PageElement): PageElement {
  return { ...el, id: crypto.randomUUID() };
}

function clonePageWithNewIds(page: PageDocument, order: number): PageDocument {
  return {
    ...page,
    id: crypto.randomUUID(),
    order,
    elements: page.elements.map(cloneElementWithNewId),
  };
}

function nextZIndex(elements: PageElement[]): number {
  return elements.reduce((max, e) => Math.max(max, e.zIndex), -1) + 1;
}

function getElementsRef(project: PhotobookProject, target: SelectionTarget): PageElement[] | null {
  if (target.scope === 'page') {
    const page = project.pages.find((p) => p.id === target.pageId);
    return page ? page.elements : null;
  }
  return project.cover.panels[target.panel].elements;
}

interface ProjectStoreState {
  project: PhotobookProject | null;
  assetsRuntime: Record<string, AssetRuntime>;
  status: ProjectStatus;
  saveStatus: SaveStatus;
  cloudSyncStatus: CloudSyncStatus;
  cloudSyncError: string | null;
  loadError: string | null;
  selection: Selection;
  view: ViewState;
  zoom: number;
  cropElementId: string | null;
  canUndo: boolean;
  canRedo: boolean;
  lastSavedAt: string | null;

  loadProject: (projectId: string) => Promise<void>;
  hydrateNewProject: (
    project: PhotobookProject,
    assetsRuntime?: Record<string, AssetRuntime>,
  ) => Promise<void>;
  reset: () => void;

  mutate: (label: string, updater: (draft: PhotobookProject) => void) => void;
  undo: () => void;
  redo: () => void;
  saveNow: () => Promise<void>;
  generateAndStoreThumbnail: (blob: Blob) => Promise<void>;

  setTitle: (title: string) => void;
  toggleSnapEnabled: () => void;
  addPage: (afterIndex?: number) => string;
  removePage: (pageId: string) => void;
  duplicatePage: (pageId: string) => void;
  reorderPages: (fromIndex: number, toIndex: number) => void;
  setPageBackground: (
    target: SelectionTarget,
    background: { type: 'color' | 'none'; color: string },
  ) => void;
  applyTemplateToPage: (pageId: string, templateId: string, redistributePhotos?: boolean) => void;

  addElement: (target: SelectionTarget, element: PageElement) => void;
  updateElement: (target: SelectionTarget, elementId: string, patch: Partial<PageElement>) => void;
  removeElement: (target: SelectionTarget, elementId: string) => void;
  duplicateElement: (target: SelectionTarget, elementId: string) => void;
  setElementAssetId: (target: SelectionTarget, elementId: string, assetId: string | null) => void;
  moveElementZ: (
    target: SelectionTarget,
    elementId: string,
    direction: 'forward' | 'backward',
  ) => void;
  toggleElementLock: (target: SelectionTarget, elementId: string) => void;

  addAssets: (assets: AssetReference[], runtime: Record<string, AssetRuntime>) => void;
  removeAsset: (assetId: string) => Promise<void>;

  setSelection: (selection: Selection) => void;
  clearSelection: () => void;
  setView: (view: ViewState) => void;
  setZoom: (zoom: number) => void;
  enterCropMode: (elementId: string) => void;
  exitCropMode: () => void;
}

let history = new ProjectHistory();

// Guards against races between overlapping `loadProject`/`hydrateNewProject`
// calls (e.g. React StrictMode's mount→unmount→remount in dev, or a user
// navigating between two editors before the first load settles) — only the
// most recent call is allowed to commit its result.
let currentLoadToken = 0;

function makeAutosave(
  set: (fn: (draft: ProjectStoreState) => void) => void,
  get: () => ProjectStoreState,
  scheduleCloudSync: () => void,
) {
  return debounce(() => {
    const project = get().project;
    if (!project) return;
    set((draft) => {
      draft.saveStatus = 'saving';
    });
    saveProject(project)
      .then(() => {
        set((draft) => {
          if (draft.project?.id === project.id && draft.project.revision === project.revision) {
            draft.saveStatus = 'saved';
            draft.lastSavedAt = new Date().toISOString();
          }
        });
        if (project.ownerMode === 'cloud') scheduleCloudSync();
      })
      .catch(() => {
        set((draft) => {
          draft.saveStatus = 'error';
        });
      });
  }, 800);
}

/**
 * Pushes local edits to the cloud a beat after the local autosave settles,
 * so a burst of edits results in one network sync instead of one per
 * keystroke. Runs fully in the background — it never blocks the editor UI
 * and never throws (see `pushProjectSync`).
 */
function makeCloudAutosync(
  set: (fn: (draft: ProjectStoreState) => void) => void,
  get: () => ProjectStoreState,
) {
  return debounce(() => {
    const project = get().project;
    if (!project || project.ownerMode !== 'cloud' || !project.cloudProjectId) return;
    set((draft) => {
      draft.cloudSyncStatus = 'syncing';
      draft.cloudSyncError = null;
    });
    pushProjectSync(project).then((result) => {
      set((draft) => {
        if (draft.project?.id !== project.id) return; // superseded by a newer load
        if (result.status === 'synced') {
          draft.project = result.project;
          draft.cloudSyncStatus = 'synced';
          draft.cloudSyncError = null;
        } else if (result.status === 'conflict') {
          draft.project = result.project;
          draft.cloudSyncStatus = 'error';
          draft.cloudSyncError =
            'Er was een synchronisatieconflict. Jouw wijzigingen zijn bewaard als herstelkopie op dit apparaat.';
        } else if (result.status === 'offline') {
          draft.cloudSyncStatus = 'offline';
        } else if (result.status === 'error') {
          draft.cloudSyncStatus = 'error';
          draft.cloudSyncError = result.message;
        }
      });
    });
  }, 1500);
}

export const useProjectStore = create<ProjectStoreState>()(
  immer((set, get) => {
    const scheduleCloudSync = makeCloudAutosync(set, get);
    const scheduleAutosave = makeAutosave(set, get, scheduleCloudSync);

    return {
      project: null,
      assetsRuntime: {},
      status: 'idle',
      saveStatus: 'saved',
      cloudSyncStatus: 'idle',
      cloudSyncError: null,
      loadError: null,
      selection: { target: null, elementIds: [] },
      view: { mode: 'cover' },
      zoom: 1,
      cropElementId: null,
      canUndo: false,
      canRedo: false,
      lastSavedAt: null,

      async loadProject(projectId) {
        const token = ++currentLoadToken;
        set((draft) => {
          draft.status = 'loading';
          draft.loadError = null;
        });
        const result = await loadProjectFromRepo(projectId);
        if (token !== currentLoadToken) return; // superseded by a newer load/reset
        if (result.status !== 'ok') {
          set((draft) => {
            draft.status = 'error';
            draft.loadError =
              result.status === 'not-found'
                ? 'Dit project kan niet worden gevonden op dit apparaat.'
                : result.message;
          });
          return;
        }
        history = new ProjectHistory();
        const assetsRuntime: Record<string, AssetRuntime> = {};
        for (const asset of result.project.assets) {
          const [originalUrl, thumbUrl] = await Promise.all([
            loadAssetObjectUrl(projectId, asset.id, 'original'),
            loadAssetObjectUrl(projectId, asset.id, 'thumbnail'),
          ]);
          if (originalUrl) {
            assetsRuntime[asset.id] = {
              objectUrl: originalUrl,
              thumbnailUrl: thumbUrl ?? originalUrl,
              widthPx: asset.widthPx,
              heightPx: asset.heightPx,
            };
          }
        }
        if (token !== currentLoadToken) return; // superseded while loading assets
        set((draft) => {
          draft.project = result.project;
          draft.assetsRuntime = assetsRuntime;
          draft.status = 'ready';
          draft.saveStatus = 'saved';
          draft.cloudSyncStatus = result.project.ownerMode === 'cloud' ? 'synced' : 'idle';
          draft.cloudSyncError = null;
          draft.lastSavedAt = result.project.updatedAt;
          draft.canUndo = false;
          draft.canRedo = false;
          // Deliberately does NOT touch `view`/`selection` here: `reset()`
          // (always called before a fresh `loadProject`, via the page's mount
          // effect cleanup) already puts them at their defaults. Overwriting
          // them again on every successful load — including a load that's
          // superseded and re-run under React StrictMode's dev-only
          // fire/cleanup/fire mount check — used to race the editor's
          // deep-link effect (`/editor/:id?page=..&element=..`), which sets
          // `view`/`selection` from the URL as soon as `status` first flips
          // to `ready`: a *second*, redundant `loadProject` resolving after
          // that would silently stomp the deep-linked selection back to the
          // cover.
        });
      },

      async hydrateNewProject(project, assetsRuntime = {}) {
        const token = ++currentLoadToken;
        history = new ProjectHistory();
        await saveProject(project);
        if (token !== currentLoadToken) return; // superseded by a newer load/reset
        set((draft) => {
          draft.project = project;
          draft.assetsRuntime = assetsRuntime;
          draft.status = 'ready';
          draft.saveStatus = 'saved';
          draft.cloudSyncStatus = 'idle';
          draft.cloudSyncError = null;
          draft.lastSavedAt = new Date().toISOString();
          draft.selection = { target: null, elementIds: [] };
          draft.view = { mode: 'cover' };
          draft.canUndo = false;
          draft.canRedo = false;
        });
      },

      reset() {
        currentLoadToken += 1;
        history = new ProjectHistory();
        set((draft) => {
          draft.project = null;
          draft.assetsRuntime = {};
          draft.status = 'idle';
          draft.saveStatus = 'saved';
          draft.cloudSyncStatus = 'idle';
          draft.cloudSyncError = null;
          draft.selection = { target: null, elementIds: [] };
          draft.view = { mode: 'cover' };
          draft.canUndo = false;
          draft.canRedo = false;
          draft.cropElementId = null;
        });
      },

      mutate(label, updater) {
        const current = get().project;
        if (!current) return;
        history.begin(label, current);
        const updated = produce(current, (draft) => {
          updater(draft as PhotobookProject);
        });
        const touched = touchProject(updated);
        history.commit(touched);
        set((draft) => {
          draft.project = touched;
          draft.canUndo = history.canUndo;
          draft.canRedo = history.canRedo;
          draft.saveStatus = 'unsaved';
        });
        scheduleAutosave();
      },

      undo() {
        const current = get().project;
        if (!current) return;
        const restored = history.undo(current);
        if (!restored) return;
        set((draft) => {
          draft.project = restored;
          draft.canUndo = history.canUndo;
          draft.canRedo = history.canRedo;
          draft.saveStatus = 'unsaved';
        });
        scheduleAutosave();
      },

      redo() {
        const current = get().project;
        if (!current) return;
        const restored = history.redo(current);
        if (!restored) return;
        set((draft) => {
          draft.project = restored;
          draft.canUndo = history.canUndo;
          draft.canRedo = history.canRedo;
          draft.saveStatus = 'unsaved';
        });
        scheduleAutosave();
      },

      async saveNow() {
        const project = get().project;
        if (!project) return;
        set((draft) => {
          draft.saveStatus = 'saving';
        });
        try {
          await saveProject(project);
          set((draft) => {
            draft.saveStatus = 'saved';
            draft.lastSavedAt = new Date().toISOString();
          });
        } catch {
          set((draft) => {
            draft.saveStatus = 'error';
          });
        }
      },

      async generateAndStoreThumbnail(blob) {
        const project = get().project;
        if (!project) return;
        await saveThumbnail(project.id, blob);
      },

      setTitle(title) {
        get().mutate('Titel wijzigen', (draft) => {
          draft.title = title.trim() || draft.title;
        });
      },

      toggleSnapEnabled() {
        get().mutate('Magnetisch uitlijnen wijzigen', (draft) => {
          draft.settings.snapEnabled = !draft.settings.snapEnabled;
        });
      },

      addPage(afterIndex) {
        const state = get();
        const project = state.project;
        if (!project) return '';
        const insertAt = afterIndex === undefined ? project.pages.length : afterIndex + 1;
        const newPage = createEmptyPage(insertAt);
        state.mutate('Pagina toevoegen', (draft) => {
          draft.pages.splice(insertAt, 0, newPage);
          draft.pages = reindexPages(draft.pages);
        });
        return newPage.id;
      },

      removePage(pageId) {
        const state = get();
        if (!state.project || state.project.pages.length <= 1) return;
        state.mutate('Pagina verwijderen', (draft) => {
          draft.pages = reindexPages(draft.pages.filter((p) => p.id !== pageId));
        });
        if (state.selection.target?.scope === 'page' && state.selection.target.pageId === pageId) {
          state.clearSelection();
        }
      },

      duplicatePage(pageId) {
        get().mutate('Pagina dupliceren', (draft) => {
          const idx = draft.pages.findIndex((p) => p.id === pageId);
          if (idx === -1) return;
          const clone = clonePageWithNewIds(draft.pages[idx] as PageDocument, idx + 1);
          draft.pages.splice(idx + 1, 0, clone);
          draft.pages = reindexPages(draft.pages);
        });
      },

      reorderPages(fromIndex, toIndex) {
        get().mutate('Pagina verplaatsen', (draft) => {
          const pages = [...draft.pages];
          const [moved] = pages.splice(fromIndex, 1);
          if (!moved) return;
          pages.splice(toIndex, 0, moved);
          draft.pages = reindexPages(pages);
        });
      },

      setPageBackground(target, background) {
        get().mutate('Achtergrond wijzigen', (draft) => {
          if (target.scope === 'page') {
            const page = draft.pages.find((p) => p.id === target.pageId);
            if (page) page.background = background;
          } else {
            draft.cover.panels[target.panel].background = background;
          }
        });
      },

      applyTemplateToPage(pageId, templateId, redistributePhotos = true) {
        get().mutate('Template toepassen', (draft) => {
          const idx = draft.pages.findIndex((p) => p.id === pageId);
          if (idx === -1) return;
          draft.pages[idx] = applyTemplateToPageDoc(draft.pages[idx] as PageDocument, templateId, {
            redistributePhotos,
          });
        });
      },

      addElement(target, element) {
        get().mutate('Element toevoegen', (draft) => {
          const elements = getElementsRef(draft, target);
          if (!elements) return;
          elements.push({ ...element, zIndex: nextZIndex(elements) });
        });
      },

      updateElement(target, elementId, patch) {
        get().mutate('Element bijwerken', (draft) => {
          const elements = getElementsRef(draft, target);
          if (!elements) return;
          const idx = elements.findIndex((e) => e.id === elementId);
          if (idx === -1) return;
          elements[idx] = { ...elements[idx], ...patch } as PageElement;
        });
      },

      removeElement(target, elementId) {
        get().mutate('Element verwijderen', (draft) => {
          const elements = getElementsRef(draft, target);
          if (!elements) return;
          const idx = elements.findIndex((e) => e.id === elementId);
          if (idx === -1) return;
          const el = elements[idx];
          if (!el) return;
          if (el.type === 'photoFrame' && el.assetId) {
            const asset = draft.assets.find((a) => a.id === el.assetId);
            if (asset) asset.useCount = Math.max(0, asset.useCount - 1);
          }
          elements.splice(idx, 1);
        });
      },

      duplicateElement(target, elementId) {
        get().mutate('Element dupliceren', (draft) => {
          const elements = getElementsRef(draft, target);
          if (!elements) return;
          const el = elements.find((e) => e.id === elementId);
          if (!el) return;
          const clone: PageElement = {
            ...el,
            id: crypto.randomUUID(),
            x: el.x + 6,
            y: el.y + 6,
            zIndex: nextZIndex(elements),
          };
          elements.push(clone);
          if (clone.type === 'photoFrame' && clone.assetId) {
            const asset = draft.assets.find((a) => a.id === clone.assetId);
            if (asset) asset.useCount += 1;
          }
        });
      },

      setElementAssetId(target, elementId, assetId) {
        get().mutate('Foto toewijzen', (draft) => {
          const elements = getElementsRef(draft, target);
          if (!elements) return;
          const el = elements.find((e) => e.id === elementId);
          if (!el || el.type !== 'photoFrame') return;
          const previousAssetId = el.assetId;
          if (previousAssetId === assetId) return;
          if (previousAssetId) {
            const prevAsset = draft.assets.find((a) => a.id === previousAssetId);
            if (prevAsset) prevAsset.useCount = Math.max(0, prevAsset.useCount - 1);
          }
          if (assetId) {
            const nextAsset = draft.assets.find((a) => a.id === assetId);
            if (nextAsset) nextAsset.useCount += 1;
          }
          el.assetId = assetId;
          el.placement = {
            scale: 1,
            offsetX: 0,
            offsetY: 0,
            rotation: 0,
            flipX: false,
            flipY: false,
          };
        });
      },

      moveElementZ(target, elementId, direction) {
        get().mutate('Volgorde wijzigen', (draft) => {
          const elements = getElementsRef(draft, target);
          if (!elements) return;
          const sorted = [...elements].sort((a, b) => a.zIndex - b.zIndex);
          const idx = sorted.findIndex((e) => e.id === elementId);
          if (idx === -1) return;
          const swapWith = direction === 'forward' ? idx + 1 : idx - 1;
          if (swapWith < 0 || swapWith >= sorted.length) return;
          const a = sorted[idx];
          const b = sorted[swapWith];
          if (!a || !b) return;
          const zA = a.zIndex;
          const zB = b.zIndex;
          const elA = elements.find((e) => e.id === a.id);
          const elB = elements.find((e) => e.id === b.id);
          if (elA) elA.zIndex = zB;
          if (elB) elB.zIndex = zA;
        });
      },

      toggleElementLock(target, elementId) {
        get().mutate('Vergrendeling wijzigen', (draft) => {
          const elements = getElementsRef(draft, target);
          const el = elements?.find((e) => e.id === elementId);
          if (el) el.locked = !el.locked;
        });
      },

      addAssets(assets, runtime) {
        get().mutate('Foto’s toevoegen', (draft) => {
          draft.assets.push(...assets);
        });
        set((draft) => {
          Object.assign(draft.assetsRuntime, runtime);
        });
      },

      async removeAsset(assetId) {
        const state = get();
        const project = state.project;
        if (!project) return;
        state.mutate('Foto verwijderen', (draft) => {
          draft.assets = draft.assets.filter((a) => a.id !== assetId);
          for (const page of draft.pages) {
            for (const el of page.elements) {
              if (el.type === 'photoFrame' && el.assetId === assetId) el.assetId = null;
            }
          }
          for (const panel of Object.values(draft.cover.panels)) {
            for (const el of panel.elements) {
              if (el.type === 'photoFrame' && el.assetId === assetId) el.assetId = null;
            }
          }
        });
        set((draft) => {
          delete draft.assetsRuntime[assetId];
        });
        await removePhotoAsset(project.id, assetId);
      },

      setSelection(selection) {
        set((draft) => {
          draft.selection = selection;
        });
      },

      clearSelection() {
        set((draft) => {
          draft.selection = { target: draft.selection.target, elementIds: [] };
        });
      },

      setView(view) {
        set((draft) => {
          draft.view = view;
          draft.selection = { target: null, elementIds: [] };
          draft.cropElementId = null;
        });
      },

      setZoom(zoom) {
        set((draft) => {
          draft.zoom = Math.min(3, Math.max(0.2, zoom));
        });
      },

      enterCropMode(elementId) {
        set((draft) => {
          draft.cropElementId = elementId;
        });
      },

      exitCropMode() {
        set((draft) => {
          draft.cropElementId = null;
        });
      },
    };
  }),
);

export function getProjectHistory(): ProjectHistory {
  return history;
}
